import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const resumeId = params.get("resume");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (error) {
        // Transient fetch error — keep polling; Stripe webhook will eventually
        // flip the record to paid even if the client-side poll keeps failing.
        if (process.env.NODE_ENV !== "production") console.debug("payment poll error:", error);
      }
      if (attempts.current >= 20) {
        setStatus("timeout");
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="tome-card max-w-lg w-full rounded-sm p-8 text-center space-y-5">
        {status === "polling" && (
          <>
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.4em] gold">Verifying Covenant</div>
            <div className="font-serif-display text-2xl">Sealing your ascension...</div>
            <div className="font-mono text-xs text-stone-500 animate-pulse">Consulting Stripe's ledger</div>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.4em] gold">◆ Sovereign</div>
            <div className="font-serif-display text-3xl gold" data-testid="payment-success-confirmed">You have ascended</div>
            <p className="font-prose text-stone-300">
              {resumeId
                ? "Your bookmarked chronicle awaits. The archive opens."
                : "All worlds are yours. Hard Mode awaits. The archive opens."}
            </p>
            {resumeId ? (
              <button
                data-testid="resume-chronicle-btn"
                onClick={() => navigate(`/play/${resumeId}`)}
                className="btn-gold"
              >
                Resume Chronicle
              </button>
            ) : (
              <button data-testid="continue-to-vault" onClick={() => navigate("/app")} className="btn-gold">Enter the Vault</button>
            )}
          </>
        )}
        {status === "failed" && (
          <>
            <div className="font-serif-display text-2xl text-red-400">The rite failed</div>
            <p className="font-prose text-stone-300">Your payment did not complete.</p>
            <button onClick={() => navigate("/app")} className="btn-ghost">Return</button>
          </>
        )}
        {status === "timeout" && (
          <>
            <div className="font-serif-display text-2xl">Still verifying...</div>
            <p className="font-prose text-stone-300">This is taking longer than usual. Your access will unlock once Stripe confirms — check back shortly.</p>
            <button onClick={() => navigate("/app")} className="btn-ghost">Return</button>
          </>
        )}
      </div>
    </div>
  );
}
